import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { Upload, Loader2, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface OrganizerSettingsProps {
  name: string
  imageUrl: string
  contactEmail: string
  contactNumber: string
  logoUrl: string
  onChangeName: (v: string) => void
  onChangeImageUrl: (v: string) => void
  onChangeContactEmail: (v: string) => void
  onChangeContactNumber: (v: string) => void
  onChangeLogoUrl: (v: string) => void
  termsAndConditions: string
  onChangeTermsAndConditions: (v: string) => void
  onSave: () => void
  onClose: () => void
  onAssignEditor: (enable: boolean, email: string) => void
  galleryImages: string[]
  onChangeGalleryImages: (v: string[]) => void
  isGalleryEnabled: boolean
  onChangeIsGalleryEnabled: (enabled: boolean) => void
}

export default function OrganizerSettings({ name, imageUrl, contactEmail, contactNumber, logoUrl, onChangeName, onChangeImageUrl, onChangeContactNumber, termsAndConditions, onChangeTermsAndConditions, onSave, onClose, onAssignEditor, galleryImages, onChangeGalleryImages, isGalleryEnabled, onChangeIsGalleryEnabled }: OrganizerSettingsProps) {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    if (galleryImages.length >= 3) {
      toast.error('Maximum 3 images allowed')
      return
    }

    setUploading(true)
    const file = e.target.files[0]
    const fileExt = file.name.split('.').pop()
    const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`
    const filePath = `organizers/${fileName}`

    try {
      const { error: uploadError } = await supabase.storage.from('organizers').upload(filePath, file)
      if (uploadError) throw uploadError

      const { data } = supabase.storage.from('organizers').getPublicUrl(filePath)
      const publicUrl = data.publicUrl

      onChangeGalleryImages([...galleryImages, publicUrl])
      toast.success('Image uploaded')
    } catch (error) {
      toast.error('Error uploading image')
      console.error(error)
    } finally {
      setUploading(false)
      // Reset input
      e.target.value = ''
    }
  }

  const handleDeleteImage = (index: number) => {
    const newImages = [...galleryImages]
    newImages.splice(index, 1)
    onChangeGalleryImages(newImages)
  }
  const [editorEmail, setEditorEmail] = useState('')
  const [suggestions, setSuggestions] = useState<Array<{ user_id: string; email: string; role: string }>>([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
          <input value={name} onChange={e => onChangeName(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Banner Image URL</label>
          <input value={imageUrl} onChange={e => onChangeImageUrl(e.target.value)} className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contact Email</label>
          <input
            type="email"
            value={contactEmail}
            disabled
            tabIndex={-1}
            aria-disabled
            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-70 pointer-events-none select-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contact Number</label>
          <input
            type="tel"
            value={contactNumber}
            onChange={e => onChangeContactNumber(e.target.value)}
            className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logo URL</label>
          <input
            value={logoUrl}
            disabled
            tabIndex={-1}
            aria-disabled
            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-70 pointer-events-none select-none"
          />
        </div>
        <div className="col-span-1 md:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gallery Images (Max 3)</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show in Banner</span>
              <button
                onClick={() => onChangeIsGalleryEnabled(!isGalleryEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isGalleryEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isGalleryEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 group">
                <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => handleDeleteImage(idx)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {galleryImages.length < 3 && (
              <label className="aspect-video flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                {uploading ? (
                  <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Upload Image</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Terms and Conditions</label>
          <textarea
            value={termsAndConditions}
            onChange={e => onChangeTermsAndConditions(e.target.value)}
            rows={6}
            className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            placeholder="Enter terms and conditions for your events..."
          />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={onSave} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">Save</button>
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 rounded-lg">Close</button>
      </div>
      {/* <div className="mt-8 border-t border-gray-700 pt-6">
        <h3 className="text-lg font-semibold text-white mb-4">Assign Editors</h3>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <input
              placeholder="type name or email"
              value={editorEmail}
              onChange={async e => {
                const v = e.target.value
                setEditorEmail(v)
                if (v.length >= 2) {
                  const { data } = await supabase.rpc('search_users_for_organizer', { term: v, limit_count: 8 })
                  const rows = (data || []) as Array<{ user_id: string; email: string; role: string | null }>
                  setSuggestions(rows.map(d => ({ user_id: d.user_id, email: d.email, role: d.role || '' })))
                  setShowSuggestions(true)
                } else {
                  setShowSuggestions(false)
                  setSuggestions([])
                }
              }}
              onFocus={() => setShowSuggestions(suggestions.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10">
                {suggestions.map(s => (
                  <button
                    key={s.user_id}
                    type="button"
                    onMouseDown={() => {
                      setEditorEmail(s.email)
                      setShowSuggestions(false)
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-700"
                  >
                    <span className="text-white">{s.email}</span>
                    {s.role && <span className="text-gray-400 ml-2">· {s.role}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => onAssignEditor(true, editorEmail)} className="px-4 py-2 bg-green-600 text-white rounded-lg">Assign</button>
          <button onClick={() => onAssignEditor(false, editorEmail)} className="px-4 py-2 bg-red-600 text-white rounded-lg">Remove</button>
        </div>
        <p className="text-sm text-gray-400 mt-2">Assigning sets role to editor; removing clears role.</p>
      </div> */}
    </>
  )
}
